/**
 * Pure math helpers behind `DensitySparkline`.
 *
 * Extracted into a non-React module so the projection from raw note-on
 * counts to pixel-space bar heights can be unit-tested in the Jest `node`
 * environment without DOM / React.
 *
 * See spec §6.2 (global bar) and §6.3 (per-track sparkline) for the exact
 * scaling decisions encoded here.
 */

/**
 * Project a single bucket count to a bar height in pixels.
 *
 * Scale is `log1p` so a single loud measure does not crush every other
 * bar to a hairline. Empty buckets render as a 1px floor (so the user
 * can still see the bar grid).
 *
 * @param count   note-on count in the bucket
 * @param maxCount overall max across the bucket array (≥ 1 enforced)
 * @param height  available pixel height
 * @returns integer pixel height in [1, height]
 */
export function bucketBarHeight(count: number, maxCount: number, height: number): number {
  if (count <= 0) {
    return 1;
  }

  const safeMax = Math.max(maxCount, 1);
  const scaled = Math.log1p(count) / Math.log1p(safeMax);
  const raw = scaled * height;

  return Math.max(1, Math.min(height, Math.round(raw)));
}

/**
 * Compute every bar height for a sparkline in one pass. Also returns the
 * `nonEmptyCount` so the caller can apply the "single non-empty bucket"
 * fallback (centre bar at 50%, see spec §8 edge case).
 */
export function bucketBarHeights(
  buckets: readonly number[],
  height: number,
): { heights: number[]; nonEmptyCount: number; maxCount: number } {
  let maxCount = 0;
  let nonEmptyCount = 0;

  for (const c of buckets) {
    if (c > 0) {
      nonEmptyCount += 1;

      if (c > maxCount) {
        maxCount = c;
      }
    }
  }

  const heights = buckets.map((c) => bucketBarHeight(c, maxCount, height));

  return { heights, nonEmptyCount, maxCount };
}

/**
 * Resolve the fallback layout used when 0 or 1 bucket has notes
 * (avoids the visually broken "single tiny bar at index 0" look).
 *
 * Returns either a per-bucket height vector (normal path) or
 * `{ kind: "centred", height }` so the caller can render a single bar
 * centred horizontally at 50% of the available height.
 */
export type SparklineLayout =
  | { kind: "bars"; heights: number[]; maxCount: number }
  | { kind: "centred"; height: number };

export function resolveSparklineLayout(
  buckets: readonly number[],
  height: number,
): SparklineLayout {
  const { heights, nonEmptyCount, maxCount } = bucketBarHeights(buckets, height);

  if (nonEmptyCount <= 1) {
    return { kind: "centred", height: Math.max(1, Math.round(height * 0.5)) };
  }

  return { kind: "bars", heights, maxCount };
}
